// When Neo greets the owner on the boat's home (TOM-920, cloud ADR 0451): on
// their first visit to the boat in this browser, and whenever at least
// GREETING_GAP_MS has passed since they last had any of the boat's pages on
// screen. The shell stamps the time while a boat page is visible and when the
// tab is hidden, and asks this whether the gap has passed when the boat opens
// and when the tab comes back into view. The server posts at most one greeting
// per owner per boat in the same gap, whatever the browsers ask.
//
// A convenience kept in this browser's storage: a storage that refuses only
// means the owner is greeted as on a first visit.

import { api } from '../api';

/** Matches the server's NEO_GREETING_GAP_MS. */
export const GREETING_GAP_MS = 4 * 3_600_000;

/** How often a visible boat page stamps the time. */
export const SEEN_STAMP_MS = 60_000;

const SEEN_PREFIX = 'nm.boatSeen.';

type SeenStorage = Pick<Storage, 'getItem' | 'setItem'>;

function storage(): SeenStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** When the owner last had one of the boat's pages on screen here, or null on a first visit. */
export function readBoatSeen(boatId: string, store: SeenStorage | null = storage()): number | null {
  try {
    const raw = store?.getItem(`${SEEN_PREFIX}${boatId}`);
    if (raw === null || raw === undefined) return null;
    const ms = Number(raw);
    return Number.isFinite(ms) && ms > 0 ? ms : null;
  } catch {
    return null;
  }
}

export function writeBoatSeen(
  boatId: string,
  atMs: number,
  store: SeenStorage | null = storage()
): void {
  try {
    store?.setItem(`${SEEN_PREFIX}${boatId}`, String(Math.round(atMs)));
  } catch {
    // Private mode or a full store: the next visit greets as a first one.
  }
}

/**
 * True when the owner should be greeted: a first visit, or a return after the
 * gap. A stamp from the future (a clock moved back) counts as a return.
 */
export function greetingDue(lastSeenMs: number | null, nowMs: number): boolean {
  if (lastSeenMs === null) return true;
  return nowMs - lastSeenMs >= GREETING_GAP_MS || lastSeenMs - nowMs > GREETING_GAP_MS;
}

/** The owner's own zone, which the greeting's "Good morning" reads; UTC when unknown. */
export function ownTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Ask the cloud for the greeting (contract §12.21). True when one was queued;
 * false when one landed recently or the request failed. A Try it guest's is
 * counted as one of their Neo turns (cloud ADR 0452).
 */
export async function requestGreeting(boatId: string): Promise<boolean> {
  try {
    const body = await api<{ queued?: unknown }>(
      `/v1/boats/${encodeURIComponent(boatId)}/neo/greeting`,
      { method: 'POST', body: { timeZone: ownTimeZone() } }
    );
    return body?.queued === true;
  } catch {
    return false;
  }
}
